import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, Crown, Star, Feather } from "lucide-react";
import { PhotoWatermark } from "../components/PhotoElements";

const OFFICERS = [
  {
    name: "Roosevelt T. Hatcher Jr.",
    role: "President",
    bio: "Pastor, Judah Family Worship Center",
  },
  {
    name: "Clarissa Lester Kenty",
    role: "Vice President",
    bio: "Media Personality, Marketing Specialist, Motivational Speaker and Business Strategist",
  },
];

const MEMBERS = [
  { name: "Marc Raby", bio: "Artistic Director, Encore Theatre & Gallery" },
  { name: "Autumn Brown Kisor", bio: "Educator and Theatre Professional" },
  { name: "Chalethia Williams", bio: "Theatre Instructor, University of Montevallo" },
  { name: "Cheryl Hall", bio: "Retired Theatre Professor, University of Alabama at Birmingham" },
  { name: "Dr. Charlena Morton", bio: "Corporate Professional" },
  { name: "Dr. Shawn Burl", bio: "Dentist" },
  { name: "Jack Cannon", bio: "Retired Theatre Professor, University of Alabama at Birmingham" },
  { name: "Joyce Silmon Horton", bio: "Realtor" },
  { name: "Lady Gwen Edwards", bio: "Pastor and Administrator" },
  {
    name: "Pastor Sean Edwards",
    bio: "Retired Captain, Birmingham Police Department; Retired Major, U.S. Army Reserve",
  },
  { name: "Sonya R. Johnson", bio: "Tenured Elementary School Educator" },
];

const IN_MEMORIAM = [
  { name: "Annie Joe Edwards", bio: "Piano and Voice Instructor; Broadway Alumna" },
  { name: "Sylvester Rickey Powell", bio: "Actor; Broadway Alumnus" },
];

// Extract initials for the monogram plate
const initials = (name) =>
  name
    .replace(/Dr\.|Pastor|Lady/gi, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

function Monogram({ name, tone = "gold" }) {
  const bg =
    tone === "officer"
      ? "linear-gradient(135deg, #7A431D 0%, #563727 100%)"
      : tone === "memoriam"
        ? "linear-gradient(135deg, #3A2C25 0%, #211a1c 100%)"
        : "linear-gradient(135deg, #563727 0%, #372C2E 100%)";
  return (
    <div
      aria-hidden
      className="w-16 h-16 md:w-20 md:h-20 flex-shrink-0 rounded-full flex items-center justify-center border border-[#DE9E48]/30 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.6)]"
      style={{ background: bg }}
    >
      <span className="font-display text-xl md:text-2xl tracking-tight text-[#DE9E48]">
        {initials(name)}
      </span>
    </div>
  );
}

function Card({ person, tone = "member", index = 0 }) {
  const italic = tone === "memoriam";
  return (
    <article
      data-testid={`board-card-${person.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="group relative border border-white/8 hover:border-[#DE9E48]/40 bg-[#3A2C25]/40 hover:bg-[#3A2C25]/70 backdrop-blur-sm rounded-none p-6 md:p-8 transition-all duration-500"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* subtle top-left hairline that grows on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 h-[2px] w-8 bg-[#DE9E48] transition-all duration-500 group-hover:w-24"
      />
      <div className="flex items-start gap-5">
        <Monogram name={person.name} tone={tone} />
        <div className="flex-1 min-w-0">
          {person.role && (
            <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-2">
              — {person.role}
            </p>
          )}
          <h3
            className={
              "font-display text-2xl md:text-[1.65rem] leading-tight tracking-tight text-white " +
              (italic ? "italic" : "")
            }
          >
            {person.name}
          </h3>
          <p className="font-body text-sm md:text-[0.95rem] leading-relaxed text-stone-300 mt-2">
            {person.bio}
          </p>
        </div>
      </div>
    </article>
  );
}

function SectionHeading({ eyebrow, title, icon: Icon, count }) {
  return (
    <div className="flex items-end justify-between gap-6 mb-10 md:mb-14 border-b border-[#DE9E48]/15 pb-6">
      <div>
        <p className="font-overline text-[0.7rem] tracking-[0.3em] text-[#DE9E48] mb-3 inline-flex items-center gap-2">
          <Icon size={14} strokeWidth={1.75} />
          {eyebrow}
        </p>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-white">
          {title}
        </h2>
      </div>
      {typeof count === "number" && (
        <p className="hidden md:block font-overline text-[0.65rem] tracking-[0.28em] text-stone-500 tabular-nums pb-2">
          {String(count).padStart(2, "0")} · Total
        </p>
      )}
    </div>
  );
}

export default function Board() {
  const totalActive = OFFICERS.length + MEMBERS.length;

  return (
    <div data-testid="board-page" className="pt-28 pb-24 bg-[#211a1c] text-stone-300 min-h-screen relative overflow-hidden">
      {/* Photo watermark — a subtle theatrical presence */}
      <PhotoWatermark index={5} position="bottom-right" opacity={0.1} />
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[80rem] h-[40rem] opacity-[0.06] blur-3xl"
        style={{ background: "radial-gradient(ellipse at top, #DE9E48 0%, transparent 60%)" }}
      />

      <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
        {/* Back link */}
        <div className="flex flex-wrap items-center gap-3 mb-10">
          <Link
            to="/about"
            data-testid="board-back-link"
            className="inline-flex items-center gap-2 font-overline text-[0.7rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to About
          </Link>
        </div>

        {/* Page hero */}
        <header className="mb-20 md:mb-28">
          <p className="font-overline text-[0.7rem] tracking-[0.3em] text-[#DE9E48] mb-5">
            — Leadership
          </p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tighter text-white mb-8 max-w-4xl">
            Board of <span className="italic text-[#DE9E48]">Directors.</span>
          </h1>
          <p className="font-body text-lg md:text-xl leading-relaxed text-stone-300 max-w-3xl">
            The stewards, artists, educators, and community leaders who guide
            Encore Theatre &amp; Gallery — protecting the mission and shaping
            the story we tell together.
          </p>
          <div className="mt-10 flex items-center gap-6">
            <div className="w-16 h-[2px] bg-[#DE9E48]" />
            <span className="font-overline text-[0.62rem] tracking-[0.28em] text-stone-500 tabular-nums">
              {String(totalActive).padStart(2, "0")} Members · Est. 2018
            </span>
          </div>
        </header>

        {/* Officers */}
        <section id="officers" className="mb-24 md:mb-32">
          <SectionHeading
            eyebrow="Board Officers"
            title="Officers."
            icon={Crown}
            count={OFFICERS.length}
          />
          <div className="grid md:grid-cols-2 gap-5">
            {OFFICERS.map((p, i) => (
              <Card key={p.name} person={p} tone="officer" index={i} />
            ))}
          </div>
        </section>

        {/* Members */}
        <section id="members" className="mb-24 md:mb-32">
          <SectionHeading
            eyebrow="Board Members"
            title="Members."
            icon={Users}
            count={MEMBERS.length}
          />
          <div className="grid md:grid-cols-2 gap-5">
            {MEMBERS.map((p, i) => (
              <Card key={p.name} person={p} tone="member" index={i} />
            ))}
          </div>
        </section>

        {/* In Memoriam */}
        <section id="in-memoriam" className="mb-16">
          <SectionHeading
            eyebrow="Legacy · In Memoriam"
            title="In Memoriam."
            icon={Feather}
          />
          <div className="grid md:grid-cols-2 gap-5">
            {IN_MEMORIAM.map((p, i) => (
              <Card key={p.name} person={p} tone="memoriam" index={i} />
            ))}
          </div>
          <p className="mt-10 font-body italic text-stone-400 text-sm md:text-base border-l-2 border-[#DE9E48]/40 pl-5 max-w-2xl">
            Their voices, artistry, and generosity remain woven into the fabric
            of Encore. We hold their memory with gratitude.
          </p>
        </section>

        {/* Closing signature block */}
        <div className="mt-24 md:mt-32 border-t border-[#DE9E48]/15 pt-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-2 inline-flex items-center gap-2">
              <Star size={12} strokeWidth={2} />
              A Tapestry of Voices
            </p>
            <p className="font-display italic text-2xl md:text-3xl leading-tight text-white max-w-xl">
              Guided by community, sustained by purpose.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="board-contact-cta"
            className="inline-flex items-center gap-3 px-7 py-3.5 bg-[#7A431D] hover:bg-[#5C331A] text-white font-overline text-xs tracking-[0.22em] uppercase rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(222,158,72,0.5)]"
          >
            Contact the Board
          </Link>
        </div>
      </div>
    </div>
  );
}
